import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { getScoreColorAndValue, processCompareData } from '../helper';
import { makeStyles } from '@material-ui/core/styles';
import { COLOR_TEXT_4, LIGHT_COMPARE_BACKGROUND_COLOR } from 'src/colors';
import CloseIcon from '@mui/icons-material/Close';
import { COLOR_PRIMARY, exchangeRates } from 'src/const';
import LaunchIcon from '@mui/icons-material/Launch';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  abbreviateNumber,
  convertCurrency,
  getCountryDataFromCountryCode,
} from 'src/utils/functions';

const useStyles = makeStyles(theme => ({
  progress: {
    position: 'relative',
    float: 'left',
    textAlign: 'center',
  },
  barOverflow: {
    position: 'relative',
    overflow: 'hidden',
    width: '90px',
    height: '45px',
    marginBottom: '-18px',
  },
  bar: {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '90px',
    height: '90px',
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: '10px solid #eee',
  },
}));

const ShowHalfProgressBar = ({ value, maxROI, minROI }) => {
  const classes = useStyles();
  const { color, score } = getScoreColorAndValue(value, maxROI, minROI);
  return (
    <Box>
      <Box className={classes.progress}>
        <Box className={classes.barOverflow}>
          <Box
            className={classes.bar}
            style={{
              transform: `rotate(${45 + score * 1.8}deg)`,
              borderBottomColor: color,
              borderRightColor: color,
            }} // Use style instead of sx for inline styles
          ></Box>
        </Box>
        <span style={{ fontFamily: 'Roboto', fontSize: '18px' }}>{value}x</span>
      </Box>
    </Box>
  );
};

const MarketsInComparisionBox = ({
  marketData,
  marketsToCompareList,
  setMarketsToCompareList,
  minROI,
  maxROI,
  currency,
  handleOnSampleClick,
}: {
  marketData: any;
  marketsToCompareList;
  setMarketsToCompareList;
  minROI: number;
  maxROI: number;
  currency: string;
  handleOnSampleClick: any;
}) => {
  return (
    <Grid
      item
      xs={2}
      sx={{
        backgroundColor: LIGHT_COMPARE_BACKGROUND_COLOR,
        margin: '10px',
        borderRadius: '10px',
        minWidth: '120px',
      }}
    >
      <Box sx={{ height: '90px', width: '100%' }}>
        <Box
          sx={{
            height: '20px',
            width: '100%',
            display: 'flex',
            justifyContent: 'flex-end',
            cursor: 'pointer',
          }}
          onClick={() => {
            setMarketsToCompareList(_ =>
              [...marketsToCompareList].filter(currentMarket => currentMarket[0] !== marketData[0])
            );
          }}
        >
          <CloseIcon sx={{ padding: 0 }} />
        </Box>
        <Typography
          variant="body1"
          sx={{ textAlign: 'center', fontWeight: 500, overflow: 'hidden', maxHeight: '70px' }}
        >
          {marketData[0] + (marketData[2] && marketData[2] != 0 ? ', ' + marketData[2] : '')}
        </Typography>
      </Box>
      <Box sx={{ height: '60px', width: '100%', display: 'flex', justifyContent: 'center' }}>
        <ShowHalfProgressBar value={marketData[1]} maxROI={maxROI} minROI={minROI} />
      </Box>
      {marketData.map((data, index) => {
        if (index > 4 && index < 16) {
          return (
            <Box
              sx={{
                height: '40px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  '&:hover': {
                    cursor: 'pointer',
                  },
                  filter:
                    marketData[4] && index !== 6 && index !== 14 && index !== 10 && index !== 15
                      ? `blur(3.5px)`
                      : `blur(0px)`,
                }}
                onClick={
                  marketData[4] && index !== 6 && index !== 14 && index !== 10 && index !== 15
                    ? handleOnSampleClick
                    : null
                }
              >
                {index == 6 || index == 7 || index == 13 || (index !== 10 && index == 14)
                  ? exchangeRates[currency].symbol +
                    abbreviateNumber(convertCurrency(currency, data))
                  : data}
              </Typography>
            </Box>
          );
        } else {
          return null;
        }
      })}
      <Button
        sx={{
          width: '90%',
          marginTop: '10px',
          background: COLOR_PRIMARY + '22',
          color: COLOR_PRIMARY,
          borderRadius: '10px',
          '&:hover': {
            color: '#fff',
            background: COLOR_PRIMARY,
          },
          margin: '10px',
        }}
        onClick={() => {
          const url = `/airbnb-data/${marketData[16]}/${
            marketData[2] ? marketData[2].toLowerCase() : '0'
          }/${marketData[0].toLowerCase()}`;
          window.open(url, '_blank');
        }}
      >
        <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '12px' }}>
          View Market <LaunchIcon fontSize="small" />
        </Typography>
      </Button>
    </Grid>
  );
};

const CompareTab = ({
  discoveryMarkets,
  marketsToCompareList,
  setMarketsToCompareList,
  allMarketData,
  minROI,
  maxROI,
  isMarketDataAccessible,
  freeMarketDataKey,
  currency,
  handleOnSampleClick,
  handleMarketSidebarClick,
  handleSidebarListItemClick,
  setAdvancedTab,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [currentMarketInSearch, setCurrentMarketInSearch] = useState<string | null>(null);

  const marketComparisionLabel = [
    'Name',
    'score',
    'state',
    'countryCode',
    'Net Rental Yield',
    'Yearly Revenue',
    'Property Price',
    'Annual Appreciation',
    'Population Change',
    'Regulations',
    'Income Change',
    'Property Price Change',
    'Average Expenses',
    'Daily Rate',
    'Occupancy Rate',
  ];

  const searchMarketOptions = allMarketData.map(
    marketData =>
      marketData.district + (marketData.city && marketData.city != 0 ? ', ' + marketData.city : '')
  );

  const redirectToAdvancedExplorerTab = () => {
    setAdvancedTab(0);
    handleMarketSidebarClick('advance');
    handleSidebarListItemClick(6);
  };

  return (
    <Box sx={{ padding: '10px', width: '100%' }}>
      <Typography variant="h5" sx={{ fontWeight: 500, padding: '10px 0px 10px 30px' }}>
        Market Comparison
      </Typography>
      <Box
        sx={{
          boxShadow:
            'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
          borderRadius: '20px',
        }}
      >
        <Grid container sx={{ flexWrap: 'nowrap', overflow: 'scroll' }}>
          {/* <Grid item container xs={9} sx={{ width: '100%', height: '450px' }}> */}
          {/* Label */}
          <Grid item xs={2} sx={{ margin: '10px', paddingLeft: '20px', minWidth: '150px' }}>
            <Box sx={{ height: '90px', width: '100%' }}></Box>
            <Box sx={{ height: '60px', width: '100%', display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">Score</Typography>
            </Box>
            {marketComparisionLabel.map((label, index) => {
              if (index > 3) {
                return (
                  <Box sx={{ height: '40px', width: '100%' }}>
                    <Typography variant="body2">{label}</Typography>
                  </Box>
                );
              } else {
                return null;
              }
            })}
          </Grid>

          {marketsToCompareList &&
            marketsToCompareList.map(marketToCompare => (
              <MarketsInComparisionBox
                marketData={marketToCompare}
                marketsToCompareList={marketsToCompareList}
                setMarketsToCompareList={setMarketsToCompareList}
                minROI={minROI}
                maxROI={maxROI}
                currency={currency}
                handleOnSampleClick={handleOnSampleClick}
              />
            ))}

          {/* </Grid> */}

          {/* Add market section */}
          {marketsToCompareList.length < 4 && (
            <Grid container item xs={3} sx={{ width: '100%', height: '450px', minWidth: '150px' }}>
              <Grid item xs={12}>
                <Autocomplete
                  disablePortal
                  id="combo-box-demo"
                  value={currentMarketInSearch}
                  onInputChange={(e, value) => setCurrentMarketInSearch(value)}
                  options={searchMarketOptions}
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <TextField
                      {...params}
                      inputRef={inputRef}
                      size="small"
                      label={params.inputProps.value ? '' : 'Add Market'}
                      InputLabelProps={{
                        shrink: false,
                        style: { paddingLeft: '20px', color: COLOR_PRIMARY },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start" sx={{ color: COLOR_PRIMARY }}>
                            <SearchIcon />
                          </InputAdornment>
                        ),
                        sx: {
                          color: COLOR_TEXT_4,
                          backgroundColor: '#e5f4f4',
                          borderRadius: 4,
                          padding: '0px 0px',
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none !important',
                          },
                        },
                      }}
                      sx={{
                        '& .MuiInputLabel-root.Mui-focused': {
                          opacity: '0 !important', // Hide on focus
                        },
                      }}
                    />
                  )}
                  onChange={(event, value) => {
                    if (!value || value == '') {
                      return;
                    }
                    // const indexOfCurrentMarketInMarketArray = searchMarketOptions.indexOf(value);
                    // if (indexOfCurrentMarketInMarketArray > -1) {
                    //   let isCurrentMarketAlreadyThere = false;

                    //   marketsToCompareList.forEach(market => {
                    //     if (market[0] === value) {
                    //       isCurrentMarketAlreadyThere = true;
                    //     }
                    //   });

                    //   !isCurrentMarketAlreadyThere &&
                    //     setMarketsToCompareList([
                    //       ...marketsToCompareList,
                    //       processCompareData(
                    //         allMarketData[indexOfCurrentMarketInMarketArray],
                    //         isMarketDataAccessible,
                    //         freeMarketDataKey,
                    //         currency
                    //       ),
                    //     ]);
                    // }
                    redirectToAdvancedExplorerTab();

                    setCurrentMarketInSearch(null);

                    if (inputRef && inputRef.current) {
                      inputRef.current.blur();
                    }
                  }}
                  sx={{
                    width: '100%',
                  }}
                />
              </Grid>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default CompareTab;
